/**
 * @module ScrollManager
 * @description Scroll-position tracking and smooth-scroll utilities. Provides the ScrollHint
 * overflow indicator element, Scroller class for programmatic smooth scrolling, and a
 * scrollIntoView helper with reduced-motion awareness.
 * @requires @fc/utilities
 * @customElement scroll-indicator
 */
import { debounce, throttle, prefersReducedMotion } from '@fc/utilities';

const END_DELAY = 50;

// Determine which axis overflows on an element
const axisOf = (el) => {
  const { scrollHeight: sh, clientHeight: ch, scrollWidth: sw, clientWidth: cw } = el;
  if (sh > ch && sw === cw) return 'y';
  if (sw > cw && sh === ch) return 'x';
  return sw > sh ? 'x' : 'y';
};

// Read padding-inline-start or padding-block-start as a number
const padStart = (el, axis) => {
  const prop = axis === 'x' ? 'paddingInlineStart' : 'paddingBlockStart';
  return parseFloat(getComputedStyle(el)[prop]) || 0;
};

// --- ScrollHint custom element ---

class ScrollHint extends HTMLElement {
  #ro = new ResizeObserver(() => this.#refresh());

  connectedCallback() {
    this.addEventListener('scroll', this.#refresh, { passive: true });
    this.#ro.observe(this);
  }

  disconnectedCallback() {
    this.removeEventListener('scroll', this.#refresh);
    this.#ro.disconnect();
  }

  #refresh = () => {
    const {
      scrollTop: st,
      scrollHeight: sh,
      clientHeight: ch,
      scrollLeft: sl,
      scrollWidth: sw,
      clientWidth: cw,
    } = this;

    const dir = sw > cw ? 'horizontal' : 'vertical';
    const range = dir === 'vertical' ? sh - ch : sw - cw;
    if (range <= 0) return;

    const pct = (dir === 'vertical' ? st : sl) / range;
    if (Number.isNaN(pct)) {
      this.style.maskImage = '';
      return;
    }

    const side = dir === 'vertical' ? 'bottom' : 'right';
    const fadeIn = pct < 0.1 ? pct * 100 : 10;
    const fadeOut = pct > 0.9 ? pct * 100 : 90;

    this.style.maskImage = `linear-gradient(
      to ${side},
      transparent ${pct > 0 ? 1 : 0}%,
      black ${fadeIn}%,
      black ${fadeOut}%,
      transparent 100%
    )`;
  };
}

if (!customElements.get('scroll-indicator')) {
  customElements.define('scroll-indicator', ScrollHint);
}

// --- Scroller class ---

export class Scroller {
  element;

  #promise;
  #resolve;
  #throttledCb;
  #endCb;
  #onStart;
  #onEnd;
  #userEvt = true;
  #skip = false;
  #active = false;

  constructor(element, options) {
    const { onScroll, onScrollStart, onScrollEnd } = options;

    this.element = element;
    this.#throttledCb = throttle(onScroll, END_DELAY);
    this.#endCb = onScroll;
    this.#onStart = onScrollStart;
    this.#onEnd = onScrollEnd;

    this.element.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  get axis() {
    return axisOf(this.element);
  }

  get finished() {
    return this.#promise ?? Promise.resolve();
  }

  get #edge() {
    return this.axis === 'x' ? 'Left' : 'Top';
  }

  // Scroll to an absolute position or element
  async to(input, options) {
    let val;

    if (input instanceof HTMLElement) {
      val = input[`offset${this.#edge}`] - padStart(this.element, this.axis);
    } else if (typeof input === 'number') {
      val = input;
    } else {
      return;
    }

    const cur = this.element[`scroll${this.#edge}`];

    if (cur !== val) {
      this.#exec({ ...options, method: 'scrollTo', val });
    } else if (this.#active) {
      // Position unchanged but scroll was in progress — force end
      this.#scrollEnd(false);
    }
  }

  // Scroll by a relative offset
  by(val, opts) {
    if (typeof val !== 'number') return;
    this.#exec({ ...opts, method: 'scrollBy', val });
  }

  set snap(on) {
    this.#skip = true;
    this.element.style.setProperty('scroll-snap-type', on ? `${this.axis} mandatory` : 'none');
  }

  destroy() {
    this.#flush();
    this.element.removeEventListener('scroll', this.#onScroll);
  }

  // --- private helpers ---

  #init() {
    if (this.#promise) return;
    this.#promise = new Promise((r) => {
      this.#resolve = r;
    });
  }

  #flush = () => {
    this.#scrollEnd.cancel();
    this.#resolve?.();
    this.#promise = undefined;
    this.#resolve = undefined;
    this.#userEvt = true;
    this.#skip = false;
  };

  #onScroll = () => {
    if (!this.#active) {
      this.#onStart?.();
      this.#active = true;
    }

    if (this.#skip) {
      this.#flush();
      this.#scrollEnd(false);
      return;
    }

    const fromUser = this.#userEvt;
    this.#init();
    if (fromUser) this.#throttledCb();
    this.#scrollEnd(fromUser);
  };

  #scrollEnd = debounce((fromUser) => {
    this.#resolve?.();
    if (fromUser) this.#endCb();
    this.#flush();

    if (this.#active) {
      this.#onEnd?.();
      this.#active = false;
    }
  }, END_DELAY);

  #exec({ method, val, instant = prefersReducedMotion() }) {
    this.#flush();
    this.#skip = instant;
    this.#userEvt = false;

    const cur = this.element[`scroll${this.#edge}`];
    const target = method === 'scrollBy' ? cur + val : val;
    if (Math.abs(target - cur) < 1) return;

    if (!instant) this.#init();

    this.element[method]({
      [this.#edge.toLowerCase()]: val,
      behavior: instant ? 'instant' : 'smooth',
    });
  }
}

// --- scrollIntoView utility ---

export function scrollIntoView(element, { ancestor, behavior = 'smooth', block = 'start', inline = 'start' } = {}) {
  if (!element) return;

  if (!ancestor) {
    element.scrollIntoView({ behavior, block, inline });
    return;
  }

  const eRect = element.getBoundingClientRect();
  const aRect = ancestor.getBoundingClientRect();

  const calcOffset = (align, aStart, aLen, eStart, eLen, cur) => {
    if (align === 'start') return cur + eStart - aStart;
    if (align === 'center') return cur + eStart - aStart - aLen / 2 + eLen / 2;
    if (align === 'end') return cur + eStart - aStart - aLen + eLen;
    return cur;
  };

  const top =
    ancestor.scrollHeight > ancestor.clientHeight
      ? calcOffset(block, aRect.top, ancestor.clientHeight, eRect.top, eRect.height, ancestor.scrollTop)
      : ancestor.scrollTop;

  const left =
    ancestor.scrollWidth > ancestor.clientWidth
      ? calcOffset(inline, aRect.left, ancestor.clientWidth, eRect.left, eRect.width, ancestor.scrollLeft)
      : ancestor.scrollLeft;

  ancestor.scrollTo({ top, left, behavior });
}
